import { Component, OnInit, OnDestroy} from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';

import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';

import { Subscription } from 'rxjs';

import { AdministrativeBoundaryService } from '@app/services/administrative-boundary.service';
import { NewSearchService, NewSearchRequestModel, NewSearchResultModel } from '@app/services/new-search.service';
import { ActivityService } from '@app/services';

import County from '@app/models/county';
import StateFIPS from '@app/models/state-fips';
import { Activity } from '@app/models';

import { SpinnerComponent } from '@app/common-components/spinner/spinner.component';
import { NewSearchResultComponent } from './new-search-result/new-search-result.component';
import { UsMapComponent } from "./us-map/us-map.component";
import { StateCountiesComponent } from './state-counties/state-counties.component';
import { PromoBannerComponent } from '@app/promo-banner/promo-banner.component';



@Component({
    selector: 'new-search',
    templateUrl: './new-search.component.html',
    styleUrls: ['./new-search.component.css'],
    imports: [NgFor, NgIf, NewSearchResultComponent, UsMapComponent, StateCountiesComponent, MatFormFieldModule, MatSelectModule, FormsModule, ReactiveFormsModule, SpinnerComponent, CommonModule, PromoBannerComponent, RouterLink]
})
export class NewSearchComponent implements OnInit {
  public counties: County[] = [];
  public states: StateFIPS[] = [];

  // holds two digit FIPS code (may have leading zero) for selected state
  private _selectedState: string = '0';
  private _selectedCounty: string = '000';
  public selectedStateName: string = '';
  public selectedCountyName: string = '';

  public selectedOfferingType: string = "All";
  
  public activitySelect = new FormControl('');
  public activities: Activity[] = [];
  public selectedActivityIds: number[] = [];

  public listings: NewSearchResultModel[] = [];
  
  public loading: boolean = false;

  private searchSubscription?: Subscription;

  constructor(
    private administrativeBoundaryService: AdministrativeBoundaryService,
    private searchService: NewSearchService,
    private activityService: ActivityService,
    private route: ActivatedRoute,
    private router: Router
  ) {}


  ngOnInit(): void {
    // populate state dropdown
    this.getStates();
    // populate activity dropdown
    this.getActivities();

    
    this.selectedOfferingType = localStorage.getItem('searchOfferingType') ?? 'All';
    // read querystring vals
    this.route.paramMap.subscribe(params => {
      const stateId = params.get('stateId');
      const countyId = params.get('countyId');

      if (!stateId) {
        this.selectedState = '0';
        
      } else if (/^\d+$/.test(stateId)) {
        this.selectedState = stateId;

        if (!countyId) {
              this.selectedCounty = '000';
            } else if (/^\d+$/.test(countyId)) {
              this.selectedCounty = countyId;
            } else if (this.selectedState && this.selectedState !== '0') {
              // non-numeric: resolve via AdministrativeBoundaryService.getCountyId which returns Observable<string>
              this.administrativeBoundaryService.getCountyId(parseInt(this.selectedState, 10), countyId).subscribe({
                next: (resolved: string) => {
                  this.selectedCounty = resolved ?? '000';
                },
                error: (err: any) => {
                  console.error('Error resolving county id for', countyId, err);
                  this.selectedCounty = '000';
                }
              });
            }
        
      } else {

        // non-numeric: resolve via AdministrativeBoundaryService.getStateId which returns Observable<string>
        this.administrativeBoundaryService.getStateId(stateId).subscribe({
          next: (resolved: string) => {
            this.selectedState = resolved ?? '0';

            if (!countyId) {
              this.selectedCounty = '000';
            } else if (/^\d+$/.test(countyId)) {
              this.selectedCounty = countyId;
            } else if (this.selectedState && this.selectedState !== '0') {
              // non-numeric: resolve via AdministrativeBoundaryService.getCountyId which returns Observable<string>
              this.administrativeBoundaryService.getCountyId(parseInt(this.selectedState, 10), countyId).subscribe({
                next: (resolved: string) => {
                  this.selectedCounty = resolved ?? '000';
                },
                error: (err: any) => {
                  console.error('Error resolving county id for', countyId, err);
                  this.selectedCounty = '000';
                }
              });
            }

          },
          error: (err: any) => {
            console.error('Error resolving state id for', stateId, err);
            this.selectedState = '0';
            this.selectedCounty = '000';
          }
        });
      }

      


    });
    
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  public get selectedCounty(): string | null {
    return this._selectedCounty;
  }

  public set selectedCounty(value: string | null) {
    this._selectedCounty = value ?? '000';
    localStorage.setItem('searchCounty', this._selectedCounty);
    
    // Update selected county name
    if (this._selectedCounty !== '000') {
      const selectedCounty = this.counties.find(c => c.countyfp === this._selectedCounty);
      if (selectedCounty) {
        this.selectedCountyName = selectedCounty.name;
      } else {
        this.selectedCountyName = '';
      }
    } else {
      this.selectedCountyName = '';
    }
    
    // don't search unless a specific state is selected
    if (this.selectedState && this.selectedState !== '0') {  
      this.search();
    }
  }
 
  // selectedState is the two digit FIPS state code (may have leading zero) or NULL for no selection made yet.
  // leaving as string to preserve leading zeros
  public get selectedState(): string | null {
    return this._selectedState;
  }

  public set selectedState(value: string  | null) {
    if (this._selectedState != (value ?? '0')) {
      this._selectedState = value ?? '0';
      localStorage.setItem('searchState', this._selectedState);
      if (this._selectedState != '0') {
        const selectedState = this.states.find(st => st.fips === this._selectedState);
          if (selectedState) {
            this.selectedStateName = selectedState.stateName;
          } else {
            this.selectedStateName = '';
          }
        this.getCounties();
      }
    }
      
    
    
    
  }

  
  // this fires when user selects a value in State dropdown
  onStateSelectionChange(event: any) {
    this.router.navigate(['/newsearch', event.target.value,'000']);
    
  }

  showUsMap() {
    this.router.navigate(['/newsearch', '0', '000']);
  }

  onStateMapClicked(stateId: string) {
    this.router.navigate(['/newsearch', stateId,'000']);
  }

  onCountySelectionChange(event: any) {
    this.router.navigate(['/newsearch', this.selectedState, event.target.value]);
    
  }

  

  onOfferingTypeSelectionChange(event: any) {
    
    this.selectedOfferingType = event.target.value;
    localStorage.setItem('searchOfferingType', this.selectedOfferingType);
    this.search();
  }

  public getCounties() {
    if (!this.selectedState) {
      this.counties = [];
      this.selectedCounty = '000';
      return;
    }
    this.administrativeBoundaryService.getCountiesFromStateFIPSCode(this.selectedState).subscribe({
      next: (counties) => {
        this.counties = counties;
      },
    });
  }

  public getStates() {
    this.administrativeBoundaryService.getStates().subscribe({
      next: (states) => {
        console.log('States:', states);
        // remove Alaska and Hawaii
        this.states = states.filter(st => st.stateAbbr !== 'AK' && st.stateAbbr !== 'HI');
        if (this._selectedState != '0') {
          const selectedState = this.states.find(st => st.fips === this._selectedState);
          if (selectedState) {
            this.selectedStateName = selectedState.stateName;
          } else {
            this.selectedStateName = '';
          }
        }
      },
    });
  }

  reset () {
    localStorage.removeItem('searchOfferingType')
    this.selectedOfferingType = 'All';
    
    this.showUsMap();
  }

  navigateToLandownerProfit() {
    this.router.navigate(['/landowner/profit']);
  }

  public getActivities() {
    this.activityService.get().subscribe({
      next: (activities) => {
        console.log('Activities:', activities)
        this.activities = activities;
      },
    });
  }

  

  public onActivityClick(activityId: number, isChecked: boolean): void {
    if (isChecked) {
      this.selectedActivityIds.push(activityId);
    } else {
      this.selectedActivityIds = this.selectedActivityIds.filter(id => id !== activityId);
    }
    this.search();
  }


  public search() {
    const req: NewSearchRequestModel = {
      state: this.selectedState ?? '0',
      county: this.selectedCounty ?? '0',
      offeringType: this.selectedOfferingType,
      activities: this.selectedActivityIds.join(',')
    };
    if (req.county === '') {
      req.county = '0';
    }
    this.loading = true;
    
    // cancel any previous running searches
    this.searchSubscription?.unsubscribe();
    this.searchSubscription = this.searchService.search(req).subscribe({
      next: (data: NewSearchResultModel[]) => {
        console.log('Search results:', data);
        
        this.listings = data;        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error searching:', error);
        this.loading = false;
      }
    });
  }

  

}

